import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { projectsAPI, stageAssignmentsAPI } from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import ManageProjectLayout from "../../../components/projects/ManageProjectLayout";
import { getCurrentStageIds } from "../../../utils/currentStage";
import WorkflowGraph from "../../../components/projects/WorkflowGraph";
import ReadyToAssignTray from "../../../components/projects/ReadyToAssignTray";
import { PERMISSIONS } from "../../../constants/permissions";
import "../../../components/projects/ManageProjectPage.css";
import { formatProjectStatus } from "../../../utils/formatProjectStatus";

const GRAPH_DEFAULT_HEIGHT = 200;
const GRAPH_MIN_HEIGHT = 140;
const GRAPH_MAX_HEIGHT = 720;

export default function ManageProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [activeTab, setActiveTab] = useState("submissions");
  const [graphVisible, setGraphVisible] = useState(false);
  const [graphHeight, setGraphHeight] = useState(GRAPH_DEFAULT_HEIGHT);
  const graphRef = useRef(null);
  const graphDraggingRef = useRef(false);

  // Same drag pattern as the stages panel resizer, on the vertical axis: the
  // handle sits on the bottom edge of the graph and sets its height.
  const handleGraphDragMove = useCallback((e) => {
    if (!graphDraggingRef.current || !graphRef.current) return;
    const top = graphRef.current.getBoundingClientRect().top;
    setGraphHeight(
      Math.min(GRAPH_MAX_HEIGHT, Math.max(GRAPH_MIN_HEIGHT, e.clientY - top)),
    );
  }, []);

  const handleGraphDragEnd = useCallback(() => {
    graphDraggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleGraphDragMove);
    document.removeEventListener("mouseup", handleGraphDragEnd);
  }, [handleGraphDragMove]);

  const handleGraphDragStart = useCallback(
    (e) => {
      e.preventDefault();
      graphDraggingRef.current = true;
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleGraphDragMove);
      document.addEventListener("mouseup", handleGraphDragEnd);
    },
    [handleGraphDragMove, handleGraphDragEnd],
  );

  useEffect(() => handleGraphDragEnd, [handleGraphDragEnd]);

  const canAssign =
    user?.isAdmin ||
    (user?.permissions || []).includes(PERMISSIONS.PROJECT_ASSIGN_USERS);

  const fetchProject = async () => {
    try {
      const res = await projectsAPI.getById(projectId);
      setProject(res.data);
      setError("");
    } catch (err) {
      setError("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    if (!project) return;
    const stages = project.workflow?.definition?.stages || [];
    setSelectedStageId(
      getCurrentStageIds(project)[0] || stages[0]?.id || "",
    );
  }, [project?.id]);

  useEffect(() => {
    if (!selectedStageId || !project) return;
    const assignments = (project.stageAssignments || []).filter(
      (assignment) =>
        assignment.stageId === selectedStageId &&
        ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(assignment.status),
    );
    const allSubmitted =
      assignments.length > 0 &&
      assignments.every((assignment) => assignment.status === "SUBMITTED");
    const stageDef = project.workflow?.definition?.stages?.find(
      (stage) => stage.id === selectedStageId,
    );
    const sendForReview = stageDef?.sendForReview !== false;

    if (allSubmitted && sendForReview) setActiveTab("review");
    else if (assignments.length > 0) setActiveTab("submissions");
    else setActiveTab("assign");
  }, [selectedStageId, project?.id]);

  const handleAllocate = async (userId) => {
    if (!userId) return;
    setActionLoading(true);
    setError("");
    try {
      await projectsAPI.allocateUser(project.id, userId);
      await fetchProject();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to allocate user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAllocation = async (userId) => {
    setActionLoading(true);
    setError("");
    try {
      await projectsAPI.removeAllocation(project.id, userId);
      await fetchProject();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to remove user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignStage = async ({
    stageId,
    assigneeId,
    startDate,
    scheduledStartTime,
    isOvertime,
    existingAssignmentId,
    instruction,
  }) => {
    if (!stageId || !assigneeId) return;
    if (!startDate) {
      setError("Please select a start date");
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      let assignment;
      if (existingAssignmentId) {
        const res = await stageAssignmentsAPI.reassign(
          project.id,
          existingAssignmentId,
          {
            assigneeId,
            startDate,
            scheduledStartTime,
            isOvertime,
          },
        );
        assignment = res.data;
      } else {
        const res = await stageAssignmentsAPI.create(project.id, {
          stageId,
          assigneeId,
          startDate,
          scheduledStartTime,
          isOvertime,
        });
        assignment = res.data;
      }
      // The instruction is secondary to the allocation: if it fails to save the
      // person is still assigned, so it is reported without unwinding the
      // assignment the user just made.
      if (instruction && assignment?.id) {
        try {
          await stageAssignmentsAPI.addStageInstructions(project.id, stageId, [
            { assignmentId: assignment.id, body: instruction },
          ]);
        } catch (err) {
          setError(
            "Assigned, but the work instruction could not be saved — add it from the Work Instructions tab.",
          );
        }
      }
      await fetchProject();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to assign stage");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async (assignmentId, payload) => {
    if (!assignmentId) return;
    setActionLoading(true);
    setError("");
    try {
      await stageAssignmentsAPI.reassign(project.id, assignmentId, payload);
      await fetchProject();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to reassign");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAssignee = async (assignmentId) => {
    if (!assignmentId) return;
    setActionLoading(true);
    setError("");
    try {
      await stageAssignmentsAPI.remove(project.id, assignmentId);
      await fetchProject();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to remove assignee");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReview = async (payload) => {
    setActionLoading(true);
    setError("");
    try {
      const activeAssignment = (project.stageAssignments || []).find(
        (assignment) =>
          assignment.stageId === selectedStageId &&
          ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(assignment.status),
      );
      if (!activeAssignment) return null;

      await stageAssignmentsAPI.review(project.id, activeAssignment.id, payload);
      await fetchProject();
      return null;
    } catch (err) {
      if (
        err.response?.status === 409 &&
        err.response?.data?.code === "CONFIRM_SKIP_REQUIRED"
      ) {
        return err.response.data;
      }
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to submit review");
      return null;
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-page-loading">
        <div className="loading-spinner" />
        <p>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="manage-page-error">
        <p>{error || "Project not found"}</p>
        <button className="btn-secondary" onClick={() => navigate("/projects")}
        >
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="manage-project-page">
      <div className="manage-page-header">
        <Link className="back-link" to={`/projects/${project.id}`}>
          ← Back to Project
        </Link>
        <div className="manage-page-title-row">
          <div>
            <h1 className="manage-page-title">{project.name}</h1>
            <span className="manage-page-subtitle">
              {project.customer?.name}
              {project.workflow?.name && ` · ${project.workflow.name}`}
            </span>
          </div>
          <div className="manage-page-header-right">
            <span
              className={`project-status-badge status-${
                project.status?.toLowerCase() || "pending"
              }`}
            >
              {formatProjectStatus(project.status)}
            </span>
            <button
              className="graph-toggle-btn"
              onClick={() => setGraphVisible((value) => !value)}
            >
              {graphVisible ? "Hide Graph ▲" : "Show Graph ▼"}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="manage-page-error-banner">{error}</div>}

      {graphVisible && (
        <>
          <div
            className="manage-page-graph"
            ref={graphRef}
            style={{ height: graphHeight }}
          >
            <WorkflowGraph
              project={project}
              selectedStageId={selectedStageId}
              onSelectStage={setSelectedStageId}
            />
          </div>
          <div
            className="graph-resize-handle"
            onMouseDown={handleGraphDragStart}
            onDoubleClick={() => setGraphHeight(GRAPH_DEFAULT_HEIGHT)}
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize workflow graph"
            title="Drag to resize · double-click to reset"
          />
        </>
      )}

      {canAssign && (
        <ReadyToAssignTray project={project} onAssigned={fetchProject} />
      )}

      <div className="manage-page-body">
        <ManageProjectLayout
          project={project}
          selectedStageId={selectedStageId}
          activeTab={activeTab}
          loading={actionLoading}
          isAdmin={user.isAdmin}
          onSelectStage={setSelectedStageId}
          onTabChange={setActiveTab}
          onAllocate={handleAllocate}
          onRemoveAllocation={handleRemoveAllocation}
          onAssignStage={handleAssignStage}
          onReassign={handleReassign}
          onReschedule={fetchProject}
          onRemove={handleRemoveAssignee}
          onReview={handleReview}
        />
      </div>
    </div>
  );
}
